import React, { useState, useEffect } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';
import ProgramCard from './ProgramCard';
import StartupCard from './StartupCard';

const RecommendationEngine = ({
  userRole = 'startup',
  onViewDetails,
  onApply,
  onContact,
  onViewProfile
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trending');

  // Mock recommendations data
  const mockRecommendations = {
    trending: [
    {
      id: 'rec-1',
      title: "Global FinTech Accelerator 2024",
      company: "JPMorgan Chase",
      companyLogo: "https://images.unsplash.com/photo-1679552764936-705999cecf97",
      companyLogoAlt: "JPMorgan Chase modern office building with glass facade",
      description: "12-week intensive program for early-stage fintech startups focusing on payments, lending, and blockchain solutions. Access to mentors, customers, and $250K investment.",
      location: "New York, NY",
      duration: "12 weeks",
      funding: "$250K investment",
      participants: "15",
      status: "active",
      deadline: "2024-12-15",
      matchScore: 92,
      tags: ["FinTech", "Payments", "Blockchain", "B2B"],
      views: 1247,
      applications: 89,
      verified: true,
      matchReasons: [
      "Matches your fintech focus area",
      "Suitable for your current funding stage"]

    },
    {
      id: 'rec-2',
      title: "Healthcare Innovation Lab",
      company: "Johnson & Johnson",
      companyLogo: "https://images.unsplash.com/photo-1656431756476-4dc873d79678",
      companyLogoAlt: "Johnson & Johnson healthcare research laboratory with modern equipment",
      description: "Partnership program for digital health startups developing solutions for patient care, medical devices, and healthcare analytics.",
      location: "Boston, MA",
      duration: "6 months",
      funding: "Up to $500K",
      participants: "10",
      status: "active",
      deadline: "2024-11-30",
      matchScore: 87,
      tags: ["HealthTech", "Digital Health", "Medical Devices"],
      views: 892,
      applications: 56,
      verified: true,
      matchReasons: [
      "Strong alignment with healthcare sector",
      "Matches your technology stack"]

    }],

    personalized: [
    {
      id: 'rec-3',
      title: "AI Innovation Challenge",
      company: "Microsoft",
      companyLogo: "https://images.unsplash.com/photo-1569677917811-08ceb8e42aa3",
      companyLogoAlt: "Microsoft modern office space with AI technology displays",
      description: "Global competition for AI startups building enterprise solutions. Winners receive Azure credits, mentorship, and potential partnership opportunities.",
      location: "Seattle, WA",
      duration: "4 months",
      funding: "$100K Azure credits",
      participants: "25",
      status: "active",
      deadline: "2024-12-01",
      matchScore: 95,
      tags: ["AI/ML", "Enterprise", "Cloud", "SaaS"],
      views: 2156,
      applications: 134,
      verified: true,
      matchReasons: [
      "Perfect match for AI/ML expertise",
      "Enterprise focus aligns with your target market"]

    }],

    recent: [
    {
      id: 'rec-4',
      title: "Sustainability Tech Accelerator",
      company: "Tesla",
      companyLogo: "https://images.unsplash.com/photo-1679599767400-62f4067c8f79",
      companyLogoAlt: "Tesla sustainable energy facility with solar panels and modern architecture",
      description: "16-week program for startups developing clean energy, sustainable transportation, and environmental technology solutions.",
      location: "Austin, TX",
      duration: "16 weeks",
      funding: "$300K investment",
      participants: "12",
      status: "active",
      deadline: "2024-11-25",
      matchScore: 78,
      tags: ["CleanTech", "Sustainability", "Energy", "Transportation"],
      views: 743,
      applications: 67,
      verified: true,
      matchReasons: [
      "Growing interest in sustainability sector",
      "Good fit for your innovation profile"]

    }]

  };

  const tabs = [
  { id: 'trending', label: 'Trending', icon: 'TrendingUp' },
  { id: 'personalized', label: 'For You', icon: 'User' },
  { id: 'recent', label: 'Recently Added', icon: 'Clock' }];


  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setRecommendations(mockRecommendations?.[activeTab] || []);
      setLoading(false);
    }, 1000);
  }, [activeTab]);

  const refreshRecommendations = () => {
    setLoading(true);
    setTimeout(() => {
      setRecommendations(mockRecommendations?.[activeTab] || []);
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
            <Icon name="Sparkles" size={18} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Smart Recommendations</h2>
            <p className="text-sm text-muted-foreground">
              AI-powered matches based on your profile and preferences
            </p>
          </div>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={refreshRecommendations}
          iconName="RefreshCw"
          iconPosition="left"
          disabled={loading}>

          Refresh
        </Button>
      </div>
      {/* Tabs */}
      <div className="flex items-center space-x-1 mb-6 bg-muted p-1 rounded-lg">
        {tabs?.map((tab) =>
        <button
          key={tab?.id}
          onClick={() => setActiveTab(tab?.id)}
          className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
          activeTab === tab?.id ?
          'bg-background text-foreground shadow-sm' :
          'text-muted-foreground hover:text-foreground'}`
          }>

            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        )}
      </div>
      {/* Content */}
      {loading ?
      <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="text-muted-foreground">Loading recommendations...</span>
          </div>
        </div> :
      recommendations?.length === 0 ?
      <div className="text-center py-12">
          <Icon name="Lightbulb" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No recommendations yet</h3>
          <p className="text-muted-foreground mb-4">
            Complete your profile to get personalized recommendations
          </p>
          <Button variant="outline" iconName="User" iconPosition="left">
            Update Profile
          </Button>
        </div> :

      <div className="space-y-4">
          {recommendations?.map((item) =>
        <div key={item?.id}>
              {userRole === 'startup' ?
          <ProgramCard
            program={item}
            onViewDetails={onViewDetails}
            onApply={onApply}
            userRole={userRole} /> :


          <StartupCard
            startup={item}
            onViewProfile={onViewProfile}
            onContact={onContact}
            userRole={userRole} />

          }
            </div>
        )}
          
          {recommendations?.length > 0 &&
        <div className="text-center pt-4">
              <Button
            variant="ghost"
            iconName="ArrowRight"
            iconPosition="right">

                View All Recommendations
              </Button>
            </div>
        }
        </div>
      }
      {/* Recommendation Settings */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-foreground">Recommendation Settings</h4>
            <p className="text-xs text-muted-foreground">
              Customize how we find matches for you
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="Settings"
            iconPosition="left">

            Configure
          </Button>
        </div>
      </div>
    </div>);

};

export default RecommendationEngine;